import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { Braces, Lock } from '@/components/icons';
import { BUILTIN_VARIABLES, canBeWrittenValue } from '@1qa/shared';
import { placeholderFor } from '@/lib/testData';

export interface InsertableVariable {
  key: string;
  isSecret: boolean;
  scope: 'ORG' | 'PROJECT';
  description?: string | null;
}

/**
 * "Insert test data" popover for a step. Secrets are offered only for "Do something" steps,
 * because the engine can only type them - never show them in a page check.
 */
export function InsertVariableMenu({
  variables,
  allowSecrets,
  onInsert,
  disabled,
  selectedText,
}: {
  variables: InsertableVariable[];
  allowSecrets: boolean;
  onInsert: (name: string) => void;
  disabled?: boolean;
  /** The text selected in the step when the menu opens (it is kept as the written value). */
  selectedText?: () => string;
}) {
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  // Focus the first item when opening, for keyboard users.
  useEffect(() => {
    if (open) ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')?.focus();
  }, [open]);

  const pick = (name: string) => {
    setOpen(false);
    onInsert(name);
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus();
  };

  const project = variables.filter((v) => v.scope === 'PROJECT');
  const org = variables.filter((v) => v.scope === 'ORG');

  const item = (name: string, opts: { secret?: boolean; hint?: string | null; disabled?: boolean } = {}) => (
    <button
      key={name}
      type="button"
      role="menuitem"
      disabled={opts.disabled}
      onClick={() => pick(name)}
      title={opts.disabled ? 'Secrets can only be used in “Do something” steps' : (opts.hint ?? undefined)}
      className="flex w-full items-center gap-2 border-b border-border px-4 py-2.5 text-left text-sm text-fg-secondary transition last:border-b-0 hover:bg-fg/8 hover:text-fg focus:bg-fg/8 focus:text-fg focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <code className="font-mono text-xs text-primary">{placeholderFor(name)}</code>
      {opts.secret && <Lock className="size-3 text-warning" aria-label="secret" />}
      {opts.hint && <span className="ml-auto truncate pl-2 text-xs text-fg-muted">{opts.hint}</span>}
    </button>
  );

  const group = (label: string, children: ReactNode) => (
    <div className="border-b border-border last:border-b-0">
      <div className="px-4 pt-3 pb-1 text-[11px] font-semibold tracking-label text-fg-muted uppercase">{label}</div>
      {children}
    </div>
  );

  return (
    <div className="relative" ref={ref} onKeyDown={onKeyDown}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!open) setSelection(selectedText?.() ?? '');
          setOpen((o) => !o);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={clsx(
          'inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs text-fg-muted transition hover:text-primary disabled:opacity-40',
          open && 'text-primary',
        )}
      >
        <Braces className="size-3.5" /> Insert test data
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label="Insert test data"
          className="absolute right-0 z-30 mt-0.5 max-h-80 w-72 animate-slide-up overflow-y-auto rounded-field border border-border bg-neutral-subtle"
        >
          {canBeWrittenValue(selection) && (
            <p className="border-b border-border px-4 py-2.5 text-xs text-fg-secondary">
              Replaces “<span className="font-medium">{selection.trim().slice(0, 60)}</span>”. The text stays in the test and is used when the
              test data has no value for the name you pick (not for secrets).
            </p>
          )}
          {project.length > 0 &&
            group(
              'This project',
              project.map((v) => item(v.key, { secret: v.isSecret, hint: v.description, disabled: v.isSecret && !allowSecrets })),
            )}
          {org.length > 0 &&
            group(
              'Organization',
              org.map((v) => item(v.key, { secret: v.isSecret, hint: v.description, disabled: v.isSecret && !allowSecrets })),
            )}
          {project.length === 0 && org.length === 0 && (
            <p className="border-b border-border px-4 py-2.5 text-xs text-fg-muted">No saved test data yet. Add some in the project's “Test data” tab.</p>
          )}
          {group(
            'Built-in values',
            Object.keys(BUILTIN_VARIABLES).map((name) => item(name)),
          )}
        </div>
      )}
    </div>
  );
}
